import { Header } from '@almbrand/header';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import { PageContext } from 'context/PageContext';
import { useContext } from 'react';
import { Location, NavLink, useLocation } from 'react-router-dom';

export const HeaderComponent: React.FC<HeaderReference> = ({
	logoImage,
	logoUrl,
	headerLinks,
	loginUrl,
	loginLabelText,
	privateLabel,
	privatePageUrl,
	businessLabel,
	businessPageUrl,
}) => {
	const location: Location = useLocation();
	const rootUrl = window.location.origin;
	const { isMobile } = useDeviceQuery();
	const pageContext = useContext(PageContext);
	const theme = pageContext.theme;

	// Get icon props based on theme
	const getIconProps = () => {
		// Show icon on login button always on PSK and only on mobile for Codan
		const shouldShowIcon = (theme === 'Codan-theme' && isMobile) || theme === 'Privatsikring-theme';

		if (!shouldShowIcon) return {};

		const isPrivatsikring = theme === 'Privatsikring-theme';
		const iconSize = isPrivatsikring ? '24' : '36';

		return {
			iconProps: {
				themeName: theme,
				icon: 'lock' as const,
				width: iconSize,
				height: iconSize,
			},
		};
	};

	const createSegmentLink = (label, pageUrl) => {
		const trackingDataJson = JSON.stringify({
			event: 'trackNavigation',
			navigationAction: label ?? '',
			navigationDestination: `${rootUrl}${pageUrl.url ?? ''}`,
			navigationType: 'top navigation',
		});

		return {
			text: label ?? '',
			url: pageUrl?.url ?? '',
			isExternalLink: pageUrl?.isExternalLink ?? false,
			useVueRoute: pageUrl?.useVueRoute ?? false,
			trackEvent: trackingDataJson,
		};
	};

	const mapHeaderLinks = (links) => {
		return links
			.map((link) => ({
				text: link?.text ?? '',
				title: link?.title ?? '',
				url: link?.url ?? '',
				target: link?.target ?? '',
				isExternalLink: link?.isExternalLink ?? false,
				useVueRoute: link?.useVueRoute ?? false,
				trackEvent: JSON.stringify({
					event: 'trackNavigation',
					navigationAction: link?.text ?? '',
					navigationDestination: `${rootUrl}${link?.url ?? ''}`,
					navigationType: 'primary navigation',
				}),
			}))
			.filter((link) => link.text && link.url);
	};

	const segmentLinks = [
		privateLabel && createSegmentLink(privateLabel, privatePageUrl),
		businessLabel && createSegmentLink(businessLabel, businessPageUrl),
	].filter(Boolean);

	return (
		<Header
			logoProps={{
				logoUrl: logoImage?.url ?? '',
				link: logoUrl?.url ?? '',
				trackingEvent: {
					event: 'trackNavigation',
					navigationAction: 'Logo',
					navigationDestination: `${rootUrl}${logoUrl?.url ?? ''}`,
					navigationType: 'primary navigation',
				},
				width: isMobile && theme === 'Privatsikring-theme' ? '140px' : 'auto',
			}}
			segmentLinks={segmentLinks}
			navigationLinks={mapHeaderLinks(headerLinks)}
			buttonProps={{
				buttonType: theme === 'Codan-theme' ? 'personal' : 'secondary',
				children: loginLabelText,
				size: 'small',
				linkButtonSettings: {
					href: loginUrl?.url,
				},
				...getIconProps(),
				iconPosition: theme === 'Privatsikring-theme' ? 'right' : 'left',
				trackingEvent: {
					event: 'trackNavigation',
					navigationAction: loginLabelText ?? '',
					navigationDestination: `${rootUrl}${loginUrl?.url ?? ''}`,
					navigationType: 'primary navigation',
				},
			}}
			renderChildren={(link: Link): JSX.Element => {
				return link.text === 'Privat' ? (
					<NavLink
						className={() => !location.pathname.includes('erhverv') && 'active'}
						key={link.url}
						to={link.url}
						title={link.title}
						target={link.target}
						data-track-event={link.trackEvent}
					>
						{link.text}
					</NavLink>
				) : (
					<NavLink
						key={link.url}
						to={link.url}
						title={link.title}
						target={link.target}
						data-track-event={link.trackEvent}
					>
						{link.text}
					</NavLink>
				);
			}}
		/>
	);
};
